import { atom } from "jotai";
import { initialStepDataState } from "@/constants/initialData";
import { type StepDataState, type StepKey } from "@/types/Form";
import type { MultiStepFormType } from "@/utils/formConfig";

export type FormSession = {
  formId: string | null;
  customerId: string | null;
};

export type FormState = {
  session: FormSession;
  formData: Partial<MultiStepFormType> | null;
  stepDataState: Record<StepKey, StepDataState>;
};

const sessionMemoryAtom = atom<FormState | null>(null);

export const formStateAtom = atom<FormState>(get => {
  const inMemory = get(sessionMemoryAtom);
  if (inMemory) return inMemory;
  return {
    session: { formId: null, customerId: null },
    formData: null,
    stepDataState: initialStepDataState,
  };
});

export const setFormStateAtom = atom(null, (_get, set, next: FormState) => {
  set(sessionMemoryAtom, next);
});

export const formSessionAtom = atom(
  get => get(formStateAtom).session,
  (get, set, next: FormSession) => {
    const current = get(formStateAtom);
    set(setFormStateAtom, { ...current, session: next });
  },
);

export const formDataAtom = atom(
  get => get(formStateAtom).formData,
  (get, set, next: Partial<MultiStepFormType> | null) => {
    const current = get(formStateAtom);
    set(setFormStateAtom, { ...current, formData: next });
  },
);

export const stepDataStateAtom = atom(
  get => get(formStateAtom).stepDataState,
  (
    get,
    set,
    next:
      | Record<StepKey, StepDataState>
      | ((prev: Record<StepKey, StepDataState>) => Record<StepKey, StepDataState>),
  ) => {
    const current = get(formStateAtom);
    const prev = current.stepDataState;
    const resolved = typeof next === "function" ? next(prev) : next;
    set(setFormStateAtom, { ...current, stepDataState: resolved });
  },
);

export const formIdAtom = atom(get => get(formSessionAtom).formId);
export const customerIdAtom = atom(get => get(formSessionAtom).customerId);

export const setFormIdAtom = atom(null, (get, set, formId: string | null) => {
  const current = get(formStateAtom);
  const nextSession: FormSession = { ...current.session, formId };
  const shouldReset = formId !== current.session.formId;
  set(setFormStateAtom, {
    ...current,
    session: nextSession,
    stepDataState: shouldReset ? initialStepDataState : current.stepDataState,
    formData: shouldReset ? null : current.formData,
  });
});

export const setCustomerIdAtom = atom(null, (get, set, customerId: string | null) => {
  const current = get(formStateAtom);
  const nextSession: FormSession = { ...current.session, customerId };
  set(setFormStateAtom, { ...current, session: nextSession });
});
